import React from 'react';
import './StupidSystemDesigns.css';

const inventoryPackingDesignCode = String.raw`enum PackingStatus {
    SUCCESS,
    INSUFFICIENT_CAPACITY,
    INVALID_REQUEST
};

addBoxes(quantity, maxWeight):
    repeat quantity times:
        boxId = boxIdCounter++
        box = new Box(boxId, maxWeight)

        boxes[boxId] = box
        availableBoxes.add(boxId)

    return SUCCESS

canFit(weight):
    return currentWeight + weight <= maxWeight

addPart(partId, weight):
    if not canFit(weight):
        return failure

    packedParts.add(partId)
    currentWeight += weight
    return success

removePart(partId, weight):
    packedParts.remove(partId)
    currentWeight -= weight

assignBoxes(orderId, parts, strategy):
    validate orderId and parts

    result = strategy.pack(parts, availableBoxes)

    if result failed:
        return INSUFFICIENT_CAPACITY, []

    commit result to actual boxes
    update availableBoxes

    return SUCCESS, result

pack(parts, availableBoxes):
    items = expand quantities into individual items
    sort items by weight descending

    tempBoxes = copies of available boxes
    include newly allocated boxes in tempBoxes
    result = empty map<BoxId, List<PartId>>

    for each item in items:
        box = first available box that canFit(item.weight)

        if no box found:
            box = allocate a new box

        if no box available:
            return failure

        box.addPart(item.id, item.weight)
        result[box.id].add(item.id)

    return success, result`;

function InventoryPackingDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Inventory Packing Low Level Design</h1>
                            <p className="system-designs-subtitle">
                                Pack order parts into weight-limited boxes without leaving inventory half-changed.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Accept an order ID and parts with a part number, quantity, and unit weight.</li>
                                <li>Pack parts into boxes without exceeding any box&apos;s maximum weight capacity.</li>
                                <li>Use existing available boxes whenever possible.</li>
                                <li>If no existing box can accommodate a part, allocate a new box if available.</li>
                                <li>If packing is impossible, return an error without making partial changes.</li>
                                <li>Return a mapping of box IDs to the parts packed inside each box.</li>
                                <li>Support multiple packing algorithms in the future.</li>
                            </ul>
                            <p className="system-design-description">
                                <strong>Out of scope:</strong> Part and box dimensions.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Core Entities</h2>
                            <ul>
                                <li>
                                    <strong>PackingController:</strong> Manages box inventory and coordinates packing.
                                    <ul>
                                        <li>Fields: <code>boxIdCounter</code>, <code>availableBoxes: List&lt;BoxId&gt;</code>, <code>boxes: Map&lt;BoxId, Box&gt;</code>.</li>
                                        <li>Methods: <code>addBoxes(quantity, maxWeight)</code> and <code>assignBoxes(orderId, parts, algorithm)</code>.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Box:</strong> Stores <code>boxId</code>, <code>maxWeight</code>, <code>currentWeight</code>, and <code>packedParts: List&lt;PartId&gt;</code>.
                                    It can add a part, check whether a weight fits, and optionally remove a part for rollback.
                                </li>
                                <li><strong>Part:</strong> Contains <code>partId</code>, <code>quantity</code>, and <code>unitWeight</code>.</li>
                                <li>
                                    <strong>PackingStrategy:</strong> Defines <code>pack(parts, availableBoxes)</code> so algorithms can be swapped without changing the controller.
                                    <code>GreedyPackingStrategy</code> is the initial strategy; <code>BestFitPackingStrategy</code> is a future enhancement.
                                </li>
                            </ul>
                            <p className="system-design-description">
                                <strong>PackingStatus:</strong> SUCCESS, INSUFFICIENT_CAPACITY, INVALID_REQUEST.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Pseudocode</h2>
                            <pre className="design-code-block"><code>{inventoryPackingDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Important Design Decisions</h2>
                            <ol>
                                <li><strong>Why use PackingStrategy?</strong> It allows new or replacement packing algorithms without changing the controller.</li>
                                <li><strong>Why sort by weight descending?</strong> Heavier items are harder to fit later. Greedy packing reduces that risk but does not guarantee an optimal solution.</li>
                                <li><strong>Why use temporary boxes?</strong> If packing fails halfway through, the actual inventory remains unchanged. Commit only after every part has been assigned.</li>
                                <li><strong>How do we manage box availability?</strong> Maintain a collection of available boxes and remove boxes from it when they are no longer available for packing.</li>
                                <li><strong>What about box allocation?</strong> Distinguish boxes already in inventory from newly allocated boxes and return an error when capacity is insufficient.</li>
                                <li>
                                    <strong>What should the output look like?</strong>
                                    <pre className="design-code-block"><code>{`{
  "orderId": "ORD101",
  "status": "SUCCESS",
  "boxes": {
    "BOX1": ["A1", "A2", "B1"],
    "BOX2": ["A3", "C1"]
  }
}`}</code></pre>
                                </li>
                            </ol>
                            <p className="system-design-description">
                                <strong>Core design principle:</strong> The controller manages boxes, the Box class enforces weight limits, and the packing strategy decides how items are distributed.
                                Note that i didn't implement dimension based packing in this, thats exponentially harder. Maybe someday.
                            </p>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        I have successfully BOXed in my thoughts for inventory management in this page. Ahem, Consider this humour carefully packaged. NVM im out.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default InventoryPackingDesign;
